import type { CSSProperties } from 'react';
import { createGlobalStyle } from 'styled-components';

// =====================================================================================================================
// MEDIA QUERIES
// =====================================================================================================================

export const sizes = {
  xs: '320px',
  mobile: '576px',
  tablet: '768px',
  desktop: '992px',
  xl: '1024px',
} as const;

export const mediaQuery = {
  xs: `(min-width: ${sizes.xs})`,
  mobile: `(min-width: ${sizes.mobile})`,
  tablet: `(min-width: ${sizes.tablet})`,
  desktop: `(min-width: ${sizes.desktop})`,
  xl: `(min-width: ${sizes.xl})`,
} as const;

export function cssLimitTextToLines(lines: number, object: true): CSSProperties;
export function cssLimitTextToLines(lines: number, object?: false): string;
export function cssLimitTextToLines(lines: number, object?: boolean) {
  if (object) {
    return {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: lines,
      WebkitBoxOrient: 'vertical',
    } as CSSProperties;
  }
  return `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: ${lines};
    -webkit-box-orient: vertical;
  `;
}

// =====================================================================================================================
// GLOBAL
// =====================================================================================================================

export const GlobalStyle = createGlobalStyle`

  // Define CSS variables
  :root {
    --navigation-bar-width: 0px;
    --navigation-bar-height: 48px;

    @media ${mediaQuery.mobile} {
      --navigation-bar-height: 72px;
    }

    @media ${mediaQuery.desktop} {
      --navigation-bar-width: 250px;
    }
  }

  * {
    font-family: 'Lato', serif;
    margin: 0;
    box-sizing: border-box;
    color: ${props => props.theme.colors.text};

    /* &:focus {
      outline: none;
    } */

    &:focus-visible {
      outline: 1px solid ${props => props.theme.colors.orange.orange100};
      outline-offset: 2px;
    }
  }

  html {
    background-color: black;
  }

  body {
    position: relative;
  }

  h1 {
    //font-size: 2rem;
    font-size: var(--font-size-3xl);
  }

  h2 {
    //font-size: 1.5rem;
    font-size: var(--font-size-2xl);
  }

  p {
    font-size: var(--font-size-md)
  }

  small {
    font-size: var(--font-size-xs)
  }

  div {
    /* background-color: transparent; */
  }

  img {
    color: ${props => props.theme.colors.text};
    font-size: 12px
  }

  pre {
    font-size: ${props => props.theme.typography.body.fontSize}px;
  }

  label {
    font-size: ${props => props.theme.typography.body.fontSize}px;

    &.required:after {
      content: " *";
      color: ${props => props.theme.colors.red};
    }

    :has(+ input:required), :has(+ textarea:required), :has(+ div > .fuuze-input) {
      :after {
        content: " *";
        color: ${props => props.theme.colors.red};
      }
    }
  }

  small {
      //font-size: ${props => props.theme.typography.body2.fontSize}px;

    &.error {
      color: ${props => props.theme.colors.red};
    }
  }

  body.dialog-open {
    overflow: hidden; /* see "dialog-open" in Dialog.tsx */
  }

  input, textarea {
    border-radius: 8px;
    background-color: ${props => props.theme.colors.dark.dark100};
    border: 1px solid ${props => props.theme.colors.dark.dark400};
    font-size: ${props => props.theme.typography.body.fontSize}px;
    //padding-left: 8px;

    &:focus:focus-visible {
      outline: 2px solid ${props => props.theme.colors.dark.dark800};
    }

    &::placeholder {
      font-style: italic;
    }

    &:disabled {
      border-color: ${props => props.theme.colors.dark.dark200};
      cursor: not-allowed;
    }

    &[type="checkbox"] {
      border: 1px solid ${props => props.theme.colors.white};
      width: 24px;
      height: 24px;
      border-radius: 4px;
      appearance: none;
    }
  }

  input {
    height: 54px;
  }

  input.error, textarea.error {
    border-color: ${props => props.theme.colors.red};

    &:focus:focus-visible {
      outline-color: ${props => props.theme.colors.red};
    }
  }

  textarea {
    color: ${props => props.theme.colors.white};
    min-height: 54px;
  }

  // REACT TOOLTIP
  [data-radix-popper-content-wrapper] {
    min-width: auto !important;
  }

  // SWIPER.JS

  .swiper-pagination-bullet-active {
    background-color: ${props => props.theme.colors.dark.dark100};
  }

  // Custom helper class

  .fuuze-helper-text {
    text-decoration: underline dashed;
    text-underline-offset: 2px;
    cursor: help
  }

  .fuuze-link {
    color: ${props => props.theme.colors.orange.orange90};
    text-decoration: underline;
    cursor: pointer;
  }
`;

// =====================================================================================================================
// UTILS
// =====================================================================================================================

interface RGB {
  r: number;
  g: number;
  b: number;
}

// Convert hex color to RGB
export function hexToRgb(hex: string): RGB | undefined {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return;
  return result
    ? {
        r: Number.parseInt(result[1] || '', 16),
        g: Number.parseInt(result[2] || '', 16),
        b: Number.parseInt(result[3] || '', 16),
      }
    : undefined;
}

// Calculate YIQ value to assess brightness
export function rgbToYIQ({ r, g, b }: RGB): number {
  return (r * 299 + g * 587 + b * 114) / 1000;
}

// Get readable text color based on background color
export function getReadableTextColor(backgroundColorHex: string, threshold = 128): '#000' | '#fff' {
  const rgb = hexToRgb(backgroundColorHex);
  if (!rgb) {
    return '#000'; // default to black if something is wrong
  }
  // Return black text for light backgrounds, white text for dark backgrounds
  return rgbToYIQ(rgb) >= threshold ? '#000' : '#fff';
}

export function mergeClassNames(...args: (string | undefined)[]) {
  return args.filter(Boolean).join(' ');
}
